import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import EmailBuilder from '../components/EmailBuilder';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Mail, 
  Loader2, 
  Lock, 
  Plus, 
  Send, 
  Users, 
  BarChart3,
  Eye,
  MousePointer,
  Trash2,
  Search,
  CheckCircle2,
  AlertCircle,
  Palette,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';

const Marketing = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [limits, setLimits] = useState(null);
  const [suppressions, setSuppressions] = useState([]);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Campaign creation wizard
  const [isCreating, setIsCreating] = useState(false);
  const [creationStep, setCreationStep] = useState(1); // 1: Design, 2: Recipients, 3: Review
  const [emailBlocks, setEmailBlocks] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    html_content: '',
    recipient_ids: []
  });
  const [clientSearch, setClientSearch] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  const isPaidUser = user?.subscription_plan && user.subscription_plan !== 'free';
  
  // Helper to check if email is suppressed
  const isEmailSuppressed = (email) => {
    return suppressions.some(s => s.email.toLowerCase() === email.toLowerCase());
  };
  
  // Get branding colors from user
  const brandingColors = user?.branding ? {
    primary: user.branding.primary_color || '#E14A1B',
    secondary: user.branding.secondary_color || '#211C16',
    accent: user.branding.accent_color || '#E14A1B',
  } : null;

  useEffect(() => {
    if (isPaidUser) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isPaidUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, clientsRes, limitsRes, suppressionsRes] = await Promise.all([
        api.get('/campaigns'),
        api.get('/clients'),
        api.get('/campaigns/limits'),
        api.get('/email-suppressions').catch(() => ({ data: { suppressions: [] } }))
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data.clients || []));
      setLimits(limitsRes.data);
      setSuppressions(suppressionsRes.data.suppressions || []);
    } catch (error) {
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Exclude suppressed clients and clients without email when selecting all
      const selectableClients = filteredClients.filter(c => !isEmailSuppressed(c.email) && c.email);
      setFormData(prev => ({
        ...prev,
        recipient_ids: selectableClients.map(c => c.client_id)
      }));
    } else {
      setFormData(prev => ({ ...prev, recipient_ids: [] }));
    }
  };

  const handleClientToggle = (clientId, checked) => {
    setFormData(prev => {
      const newIds = checked 
        ? [...prev.recipient_ids, clientId]
        : prev.recipient_ids.filter(id => id !== clientId);
      return { ...prev, recipient_ids: newIds };
    });
    setSelectAll(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      html_content: '',
      recipient_ids: []
    });
    setEmailBlocks([]);
    setClientSearch('');
    setSelectAll(false);
    setCreationStep(1);
  };

  const startCreating = () => {
    resetForm();
    setIsCreating(true);
  };

  const cancelCreating = () => {
    setIsCreating(false);
    resetForm();
  };

  // Convert blocks to HTML for sending
  const blocksToHtml = (blocks) => {
    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f5f5f5; }
    .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
    .content { padding: 0; }
    p { margin: 0 0 16px 0; line-height: 1.6; }
    a { color: ${brandingColors?.primary || '#E14A1B'}; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
  <div class="container">
    <div class="content">
`;

    blocks.forEach(block => {
      switch (block.type) {
        case 'text':
          html += `<div style="padding: ${block.content.padding}px; text-align: ${block.content.alignment};">${block.content.html}</div>`;
          break;
        case 'image':
          if (block.content.src) {
            const img = `<img src="${block.content.src}" alt="${block.content.alt}" style="width: ${block.content.width}; max-width: 100%;">`;
            const wrappedImg = block.content.link ? `<a href="${block.content.link}">${img}</a>` : img;
            html += `<div style="padding: ${block.content.padding}px; text-align: ${block.content.alignment};">${wrappedImg}</div>`;
          }
          break;
        case 'button':
          html += `
<div style="padding: ${block.content.padding}px; text-align: ${block.content.alignment};">
  <a href="${block.content.link}" style="display: inline-block; padding: 12px 24px; background-color: ${block.content.backgroundColor}; color: ${block.content.textColor}; text-decoration: none; border-radius: ${block.content.borderRadius}px; font-weight: 600;">${block.content.text}</a>
</div>`;
          break;
        case 'divider':
          html += `<div style="padding: ${block.content.padding}px;"><hr style="border: none; border-top: ${block.content.thickness}px solid ${block.content.color}; width: ${block.content.width}; margin: 0 auto;"></div>`;
          break;
        case 'spacer':
          html += `<div style="height: ${block.content.height}px;"></div>`;
          break;
        case 'columns':
          html += `
<div style="padding: ${block.content.padding}px;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td width="48%" valign="top" style="padding-right: ${block.content.gap / 2}px;">${block.content.columns[0]?.html || ''}</td>
      <td width="48%" valign="top" style="padding-left: ${block.content.gap / 2}px;">${block.content.columns[1]?.html || ''}</td>
    </tr>
  </table>
</div>`;
          break;
        default:
          break;
      }
    });

    html += `
    </div>
  </div>
</body>
</html>`;

    return html;
  };

  // Extract plain text from blocks for the body field
  const blocksToText = (blocks) => {
    return blocks
      .filter(b => b.type === 'text' || b.type === 'columns')
      .map(b => {
        if (b.type === 'columns') {
          return b.content.columns.map(c => c.html.replace(/<[^>]*>/g, '')).join('\n');
        }
        return b.content.html.replace(/<[^>]*>/g, '');
      })
      .join('\n\n');
  };

  const handleSendCampaign = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (emailBlocks.length === 0) {
      toast.error('Please design your email content');
      return;
    }
    if (formData.recipient_ids.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    try {
      const htmlContent = blocksToHtml(emailBlocks);
      const textContent = blocksToText(emailBlocks);
      
      await api.post('/campaigns', {
        name: formData.name,
        subject: formData.subject,
        body: textContent,
        html_content: htmlContent,
        recipient_ids: formData.recipient_ids
      });
      
      toast.success(`Campaign sent to ${formData.recipient_ids.length} recipients`);
      setIsCreating(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send campaign'));
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      setCampaigns(prev => prev.filter(c => c.campaign_id !== campaignId));
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const viewCampaign = async (campaign) => {
    try {
      const response = await api.get(`/campaigns/${campaign.campaign_id}`);
      setSelectedCampaign(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load campaign details');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show upgrade prompt for free users
  if (!isPaidUser) {
    return (
      <Layout>
        <div className="p-6 lg:p-12 max-w-4xl mx-auto">
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Marketing Campaigns</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Your past clients are your best source of new bookings. Design emails with the drag-and-drop builder, send campaigns to your client list, and track every open and click. Included with Standard (500 emails/mo) and Pro (2,000 emails/mo).
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
                <div className="p-4 rounded-lg bg-muted/50">
                  <Palette className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="text-sm font-medium">Drag &amp; Drop Builder</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <Users className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                  <p className="text-sm font-medium">Client Targeting</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <Eye className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                  <p className="text-sm font-medium">Open Tracking</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <BarChart3 className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                  <p className="text-sm font-medium">Analytics</p>
                </div>
              </div>
              <Button onClick={() => window.location.href = '/settings'}>
                Upgrade to Standard or Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-12 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Campaign Creation Wizard
  if (isCreating) {
    return (
      <Layout>
        <div className="h-[calc(100vh-64px)] flex flex-col">
          {/* Wizard Header */}
          <div className="border-b bg-background px-6 py-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={cancelCreating}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-xl font-semibold">Create Campaign</h1>
                  <p className="text-sm text-muted-foreground">
                    Step {creationStep} of 3: {creationStep === 1 ? 'Design Email' : creationStep === 2 ? 'Select Recipients' : 'Review & Send'}
                  </p>
                </div>
              </div>
              
              {/* Step indicators */}
              <div className="hidden md:flex items-center gap-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === creationStep 
                        ? 'bg-primary text-primary-foreground' 
                        : step < creationStep 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-muted text-muted-foreground'
                    }`}>
                      {step < creationStep ? <CheckCircle2 className="w-5 h-5" /> : step}
                    </div>
                    {step < 3 && <div className={`w-12 h-0.5 ${step < creationStep ? 'bg-primary' : 'bg-muted'}`} />}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                {creationStep > 1 && (
                  <Button variant="outline" onClick={() => setCreationStep(creationStep - 1)}>
                    Back
                  </Button>
                )}
                {creationStep < 3 ? (
                  <Button onClick={() => setCreationStep(creationStep + 1)}>
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSendCampaign}
                    disabled={sending || !user?.email_verified}
                    title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                    className="gap-2"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Campaign
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-hidden">
            {/* Step 1: Design Email */}
            {creationStep === 1 && (
              <EmailBuilder
                initialBlocks={emailBlocks}
                onChange={setEmailBlocks}
                brandingColors={brandingColors}
              />
            )}

            {/* Step 2: Select Recipients */}
            {creationStep === 2 && (
              <div className="p-6 lg:p-12 max-w-4xl mx-auto overflow-auto h-full">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Select Recipients
                    </CardTitle>
                    <CardDescription>
                      Choose which clients will receive this campaign
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Search and Select All */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search clients..."
                          value={clientSearch}
                          onChange={(e) => setClientSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="selectAll"
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                        />
                        <Label htmlFor="selectAll" className="text-sm cursor-pointer">
                          Select All
                        </Label>
                      </div>
                    </div>

                    <div className="mb-4 p-3 bg-primary/5 rounded-lg">
                      <p className="text-sm font-medium">
                        {formData.recipient_ids.length} of {clients.length} clients selected
                      </p>
                    </div>

                    {/* Client List */}
                    <div className="border rounded-lg max-h-96 overflow-y-auto">
                      {filteredClients.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          {clients.length === 0 ? (
                            <>
                              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>No clients yet. Add clients first.</p>
                            </>
                          ) : (
                            <p>No clients match your search</p>
                          )}
                        </div>
                      ) : (
                        filteredClients.map((client) => {
                          const isSuppressed = isEmailSuppressed(client.email);
                          const hasNoEmail = !client.email;
                          const isDisabled = isSuppressed || hasNoEmail;
                          return (
                            <div 
                              key={client.client_id}
                              className={`flex items-center gap-3 p-4 hover:bg-muted/50 border-b last:border-b-0 ${isDisabled ? 'opacity-60 bg-muted/30' : ''}`}
                            >
                              <Checkbox
                                id={`client-${client.client_id}`}
                                checked={formData.recipient_ids.includes(client.client_id)}
                                onCheckedChange={(checked) => !isDisabled && handleClientToggle(client.client_id, checked)}
                                disabled={isDisabled}
                              />
                              <Label 
                                htmlFor={`client-${client.client_id}`} 
                                className={`flex-1 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{client.name}</p>
                                  {isSuppressed && (
                                    <Badge variant="destructive" className="text-xs">Undeliverable</Badge>
                                  )}
                                  {hasNoEmail && (
                                    <Badge variant="secondary" className="text-xs">No email</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{client.email || client.phone || 'No contact info'}</p>
                              </Label>
                              {formData.recipient_ids.includes(client.client_id) && !isDisabled && (
                                <CheckCircle2 className="w-5 h-5 text-primary" />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Unsubscribe warning */}
                    <div className="mt-4 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-600">
                          Respecting Unsubscribes
                        </p>
                        <p className="text-muted-foreground">
                          Clients who have unsubscribed will be automatically excluded from this campaign.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Review & Send */}
            {creationStep === 3 && (
              <div className="p-6 lg:p-12 max-w-4xl mx-auto overflow-auto h-full">
                <div className="space-y-6">
                  {/* Campaign Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Campaign Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="campaignName">Campaign Name *</Label>
                        <Input
                          id="campaignName"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Holiday Sale Announcement"
                        />
                        <p className="text-xs text-muted-foreground">
                          For your reference only - not shown to recipients
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Email Subject *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                          placeholder="e.g., Special Offer Just for You!"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Email Blocks</p>
                          <p className="text-2xl font-bold">{emailBlocks.length}</p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Recipients</p>
                          <p className="text-2xl font-bold">{formData.recipient_ids.length}</p>
                        </div>
                      </div>

                      {/* Warnings */}
                      {emailBlocks.length === 0 && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                          ⚠️ Your email has no content. Go back to Step 1 to design your email.
                        </div>
                      )}
                      {formData.recipient_ids.length === 0 && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                          ⚠️ No recipients selected. Go back to Step 2 to select clients.
                        </div>
                      )}
                      
                      {/* Email quota warning */}
                      {limits && formData.recipient_ids.length > limits.remaining && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                          ⚠️ This campaign has {formData.recipient_ids.length} recipients but you have {limits.remaining} emails remaining this month. Remove recipients or upgrade your plan.
                        </div>
                      )}
                      
                      {/* Quota display */}
                      {limits && limits.remaining >= formData.recipient_ids.length && formData.recipient_ids.length > 0 && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                          <p className="text-muted-foreground">
                            After sending: <span className="font-medium text-foreground">{limits.used + formData.recipient_ids.length}</span> of {limits.limit} emails used ({limits.remaining - formData.recipient_ids.length} remaining)
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // Main Marketing Dashboard
  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-7xl mx-auto">
        <EmailVerificationBanner className="mb-6" message="Verify your email to unlock sending marketing campaigns." />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Marketing</h1>
            <p className="text-muted-foreground mt-1">
              Create and send beautiful email campaigns to your clients
            </p>
          </div>
          <Button onClick={startCreating} className="gap-2">
            <Plus className="w-4 h-4" />
            New Campaign
          </Button>
        </div>

        {/* Usage Limits Card */}
        {limits && (
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Send className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Monthly Email Limit</p>
                    <p className="text-sm text-muted-foreground">
                      {limits.used} of {limits.limit} emails used this month
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{limits.remaining}</p>
                  <p className="text-sm text-muted-foreground">emails remaining</p>
                </div>
              </div>
              <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${Math.min((limits.used / limits.limit) * 100, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Campaigns
            </CardTitle>
            <CardDescription>
              View and manage your email marketing campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12">
                <Palette className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No campaigns yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first email campaign with our drag &amp; drop builder
                </p>
                <Button onClick={startCreating} variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Campaign
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Opens</TableHead>
                    <TableHead>Clicks</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.campaign_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {campaign.subject}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="gap-1">
                          <Users className="w-3 h-3" />
                          {campaign.recipient_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4 text-muted-foreground" />
                          <span>{campaign.stats?.opens || 0}</span>
                          {campaign.stats?.open_rate > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({campaign.stats.open_rate}%)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MousePointer className="w-4 h-4 text-muted-foreground" />
                          <span>{campaign.stats?.clicks || 0}</span>
                          {campaign.stats?.click_rate > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({campaign.stats.click_rate}%)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(campaign.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => viewCampaign(campaign)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* View Campaign Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedCampaign?.name}</DialogTitle>
              <DialogDescription>
                Sent on {selectedCampaign && formatDate(selectedCampaign.created_at)}
              </DialogDescription>
            </DialogHeader>

            {selectedCampaign && (
              <div className="space-y-6 py-4">
                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <Users className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-2xl font-bold">{selectedCampaign.recipient_count}</p>
                      <p className="text-xs text-muted-foreground">Sent</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <Eye className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                      <p className="text-2xl font-bold">{selectedCampaign.stats?.opens || 0}</p>
                      <p className="text-xs text-muted-foreground">Opens</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <MousePointer className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                      <p className="text-2xl font-bold">{selectedCampaign.stats?.clicks || 0}</p>
                      <p className="text-xs text-muted-foreground">Clicks</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <BarChart3 className="w-6 h-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{selectedCampaign.stats?.open_rate || 0}%</p>
                      <p className="text-xs text-muted-foreground">Open Rate</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Email Content */}
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <div className="p-3 bg-muted rounded-lg">
                    {selectedCampaign.subject}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Content Preview</Label>
                  <div className="p-4 bg-muted rounded-lg whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedCampaign.body}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Marketing;
